import React, { useEffect, useMemo, useRef } from 'react';
import { useCustomerSelector } from '../../context/CustomerSelectorContext';
import { CrudStyles } from '../shared/Crud';
import { useCrud } from '../../hooks/useCrud';
import ResponsibleReceiptForm from './ResponsibleReceiptForm';
import responsibleReceiptService from '../../services/responsibleReceiptService';
import PageWithCustomerSelector from '../PageWithCustomerSelector';
import { EditIcon, PlusIcon, SearchIcon } from '../Layout/icons';
import PhoneActionMenu from '../shared/PhoneActionMenu';
import styled from 'styled-components';

const { EmptyState, FilterGroup, StatusBadge, Container, Header, Button, FiltersContainer, TableContainer, PaginationContainer, PageInfo, PageButtons } = CrudStyles;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    min-width: 600px;
    th, td {
        padding: 14px 16px;
        text-align: left;
        border-bottom: 1px solid ${({ theme }) => theme?.colors?.gray?.[100] || '#eee'};
        vertical-align: middle;
    }
    th {
        background: ${({ theme }) => theme?.colors?.background || '#f5f5f5'};
        font-weight: 600;
        color: ${({ theme }) => theme?.colors?.gray?.[900] || '#222'};
        cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
        user-select: none;
    }
    th:hover {
        background: ${({ theme }) => theme?.colors?.gray?.[100] || '#eee'};
    }
    .sort-icon { margin-left: 4px; }
`;

const ActionCell = styled.td` white-space: nowrap; `;

const ActionBtn = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    margin: 0 4px 0 0;
    background: none;
    border: none;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme, $danger }) => ($danger ? theme?.colors?.danger?.main || '#dc2626' : theme?.colors?.primary?.main || '#3b5998')};
    cursor: pointer;
    border: 1px solid ${({ theme, $danger }) => ($danger ? theme?.colors?.danger?.main || '#dc2626' : theme?.colors?.gray?.[200] || '#e5e7eb')};
    &:hover {
        background: ${({ theme, $danger }) => ($danger ? 'rgba(220,38,38,0.08)' : theme?.colors?.primary?.light ? `${theme.colors.primary.light}25` : 'rgba(59,89,152,0.1)')};
    }
    svg { width: 16px; height: 16px; flex-shrink: 0; }
`;

function formatPhoneDisplay(pn) {
    if (!pn) return '—';
    if (typeof pn === 'string') return pn;
    const area = pn.areaCode ?? '';
    const num = pn.number ?? '';
    if (area && num) return `(${area}) ${num.length === 9 ? num.replace(/(\d{5})(\d{4})/, '$1-$2') : num}`;
    return num || area || '—';
}

/**
 * @param {Object} [props]
 * @param {boolean} [props.embedded] - Quando true, renderiza apenas o conteúdo (sem PageWithCustomerSelector). Usado dentro da aba na tela de entregas.
 */
export default function ResponsibleReceipt({ embedded = false } = {}) {
    const { selectedCustomerId, isTransportCompany, isManagingOwnData } = useCustomerSelector();
    const prevCustomerRef = useRef(selectedCustomerId);

    /** Só é possível listar/criar/editar/ativar/inativar responsáveis de clientes. Empresa de transporte não gerencia dados da própria empresa. */
    const canManageResponsibles = isTransportCompany
        ? Boolean(selectedCustomerId && !isManagingOwnData)
        : Boolean(selectedCustomerId);

    const adaptedService = useMemo(() => {
        if (!canManageResponsibles) return null;
        return {
            list: (params) =>
                responsibleReceiptService.list({
                    ...params,
                    customerId: selectedCustomerId,
                    active: params.active !== undefined && params.active !== '' ? params.active === 'true' : true,
                }),
            get: async () => ({}),
            create: (data) =>
                responsibleReceiptService.create(
                    data,
                    isTransportCompany && selectedCustomerId ? selectedCustomerId : undefined
                ),
            update: (id, data) =>
                responsibleReceiptService.update({
                    id,
                    ...data,
                    customerId: isTransportCompany && selectedCustomerId ? selectedCustomerId : undefined,
                }),
        };
    }, [isTransportCompany, selectedCustomerId, canManageResponsibles]);

    const mockService = useMemo(
        () => ({
            list: async () => ({ content: [], page: { totalPages: 0, number: 0, first: true, last: true } }),
            get: async () => ({}),
            create: async () => {},
            update: async () => {},
        }),
        []
    );

    const {
        view,
        data,
        loading,
        pagination,
        handlePageChange,
        sorting,
        handleSort,
        localFilters,
        handleFilterChange,
        handleSearch,
        editingItem,
        handleCreate,
        handleUpdate,
        handleNew,
        handleEdit,
        handleCancel,
        setRefetchTrigger,
        setView,
        setEditingItem,
    } = useCrud(adaptedService || mockService, {
        pageSize: 10,
        defaultSort: 'name,asc',
        defaultFilters: {
            name: '',
            active: 'true',
        },
    });

    useEffect(() => {
        if (prevCustomerRef.current !== selectedCustomerId) {
            prevCustomerRef.current = selectedCustomerId;
            setView('list');
            setEditingItem(null);
            setRefetchTrigger((prev) => prev + 1);
        }
    }, [selectedCustomerId, setView, setEditingItem, setRefetchTrigger]);

    useEffect(() => {
        if (!canManageResponsibles && (view === 'create' || view === 'edit')) {
            setView('list');
            setEditingItem(null);
        }
    }, [canManageResponsibles, view, setView, setEditingItem]);

    const onSortClick = (field) => {
        if (!handleSort) return;
        const isAsc = sorting?.field === field && sorting?.direction === 'asc';
        handleSort(field, isAsc ? 'desc' : 'asc');
    };

    const getSortIcon = (field) => {
        if (!sorting || sorting.field !== field) return null;
        return <span className="sort-icon">{sorting.direction === 'asc' ? ' ↑' : ' ↓'}</span>;
    };

    const columnsConfig = [
        { header: 'Nome', accessor: 'name', sortable: true },
        { header: 'Documento', accessor: 'document', sortable: false },
        { header: 'Telefone', accessor: 'phoneNumber', sortable: false },
        { header: 'Status', accessor: 'active', sortable: false },
    ];

    const innerContent =
        view === 'create' || view === 'edit' ? (
            <ResponsibleReceiptForm
                key={`${view}-${selectedCustomerId}-${editingItem?.id || 'new'}`}
                initialData={editingItem}
                onSubmit={view === 'create' ? handleCreate : handleUpdate}
                onCancel={handleCancel}
            />
        ) : !canManageResponsibles ? (
            <EmptyState>
                    <h3>
                        {isTransportCompany
                            ? 'Selecione um cliente'
                            : 'Selecione uma opção'}
                    </h3>
                    <p>
                        {isTransportCompany
                            ? 'Para gerenciar responsáveis pelo recebimento, selecione um cliente no seletor acima. Não é possível gerenciar responsáveis da própria empresa.'
                            : 'Selecione uma opção no seletor acima para gerenciar responsáveis pelo recebimento.'}
                    </p>
                </EmptyState>
            ) : (
                <Container>
                    <Header>
                        <h1>Responsáveis pelo recebimento</h1>
                        <Button variant="primary" onClick={handleNew}>
                            <PlusIcon />
                            Novo
                        </Button>
                    </Header>

                    <FiltersContainer>
                        <FilterGroup>
                            <label>Status</label>
                            <select name="active" value={localFilters.active} onChange={handleFilterChange}>
                                <option value="true">Ativo</option>
                                <option value="false">Inativo</option>
                            </select>
                        </FilterGroup>
                        <Button type="button" variant="primary" onClick={handleSearch} style={{ height: '38px' }}>
                            <SearchIcon />
                            Pesquisar
                        </Button>
                    </FiltersContainer>

                    <TableContainer>
                        <Table>
                            <thead>
                                <tr>
                                    {columnsConfig.map((col) => (
                                        <th
                                            key={col.accessor}
                                            $sortable={col.sortable}
                                            onClick={col.sortable ? () => onSortClick(col.accessor) : undefined}
                                        >
                                            {col.header}
                                            {col.sortable && getSortIcon(col.accessor)}
                                        </th>
                                    ))}
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={5} style={{ textAlign: 'center', padding: '32px' }}>
                                            Carregando...
                                        </td>
                                    </tr>
                                ) : data.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} style={{ textAlign: 'center', padding: '32px' }}>
                                            Nenhum registro encontrado
                                        </td>
                                    </tr>
                                ) : (
                                    data.map((item) => (
                                        <tr key={item.id}>
                                            <td>{item.name ?? '—'}</td>
                                            <td>{item.document ?? '—'}</td>
                                            <td>
                                                <PhoneActionMenu phoneNumber={formatPhoneDisplay(item.phoneNumber)} />
                                            </td>
                                            <td>
                                                <StatusBadge $active={item.active}>
                                                    {item.active ? 'Ativo' : 'Inativo'}
                                                </StatusBadge>
                                            </td>
                                            <ActionCell>
                                                <ActionBtn type="button" onClick={() => handleEdit(item)} title="Editar">
                                                    <EditIcon />
                                                    Editar
                                                </ActionBtn>
                                            </ActionCell>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </TableContainer>

                    {pagination && (
                        <PaginationContainer>
                            <PageInfo>
                                Página {pagination.totalPages === 0 ? 0 : pagination.page + 1} de {pagination.totalPages || 0}
                            </PageInfo>
                            <PageButtons>
                                <Button
                                    variant="outline"
                                    disabled={pagination.totalPages === 0 || pagination.first}
                                    onClick={() => handlePageChange(pagination.page - 1)}
                                >
                                    Anterior
                                </Button>
                                <Button
                                    variant="outline"
                                    disabled={pagination.totalPages === 0 || pagination.last}
                                    onClick={() => handlePageChange(pagination.page + 1)}
                                >
                                    Próxima
                                </Button>
                            </PageButtons>
                        </PaginationContainer>
                    )}
                </Container>
        );

    if (embedded) {
        return innerContent;
    }
    return <PageWithCustomerSelector>{innerContent}</PageWithCustomerSelector>;
}
